import { useTranslation } from 'react-i18next';
import { IExperience } from '../../../../interfaces/interfaces';
import companyStyle from './Company.module.scss';
import commonStyle from '../../../../styles/commonStyles.module.scss';
import { useReveal } from '../../../../hooks/useReveal';

interface IProps {
    company: IExperience;
    /** Stagger in milliseconds. */
    delay?: number;
    /** Renders the full grouped breakdown instead of the short highlights. */
    expanded?: boolean;
}

const Company = (props: IProps) => {
    const { company, delay = 0, expanded = false } = props;
    const { t } = useTranslation();
    const detailGroups = expanded ? company.details : undefined;

    // Revealed from inside the <li> so the <ol> only ever contains list items.
    const { ref, isVisible } = useReveal<HTMLLIElement>();

    return (
        <li
            ref={ref}
            className={`${companyStyle.item} ${isVisible ? companyStyle.visible : ''}`}
            style={{ transitionDelay: `${delay}ms` }}
        >
            <div className={companyStyle.rail} aria-hidden='true'>
                <span className={`${companyStyle.dot} ${company.current ? companyStyle.dotCurrent : ''}`} />
            </div>

            <div className={companyStyle.body}>
                <div className={companyStyle.head}>
                    <span className={companyStyle.period}>{company.period}</span>
                    {company.current && <span className={companyStyle.currentTag}>{t('myJourney.current')}</span>}
                </div>

                <h3 className={companyStyle.role}>{company.role}</h3>

                <p className={companyStyle.company}>
                    <span className={companyStyle.companyName}>{company.companyName}</span>
                    <span className={companyStyle.separator}>·</span>
                    <span className={companyStyle.companyNote}>{company.companyNote}</span>
                </p>

                {detailGroups ? (
                    <div className={companyStyle.detailGroups}>
                        {detailGroups.map((group) => (
                            <div key={group.title}>
                                <h4 className={companyStyle.detailTitle}>{group.title}</h4>
                                <ul className={companyStyle.highlights}>
                                    {group.items.map((item, i) => (
                                        <li key={i} className={companyStyle.highlight}>
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                ) : (
                    <ul className={companyStyle.highlights}>
                        {company.highlights.map((highlight, i) => (
                            <li key={i} className={companyStyle.highlight}>
                                {highlight}
                            </li>
                        ))}
                    </ul>
                )}

                <div className={`${commonStyle.tagRow} ${companyStyle.stack}`}>
                    {company.stack.map((tech) => (
                        <span key={tech} className={commonStyle.tag}>
                            {tech}
                        </span>
                    ))}
                </div>
            </div>
        </li>
    );
};

export default Company;
